import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Assets.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const MAX_PLANS = 15;
const NARROW = '(max-width: 699px)';

const devicesText = (n) => `${n} device${n === 1 ? '' : 's'}`;
const byOrder = (a, b) => Number(a.order) - Number(b.order);

function useNarrow() {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(NARROW).matches : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return narrow;
}

// The floors' plan pictures are behind the sign-in and a plain <img> carries
// no token, so each is fetched like any request and held as a blob URL.
function usePlanPictures(floors) {
  const [pics, setPics] = useState({});
  const key = floors.filter((f) => f.plan_url).slice(0, MAX_PLANS).map((f) => `${f.id}|${f.plan_url}`).join(',');
  useEffect(() => {
    let live = true;
    const made = [];
    setPics({});
    key.split(',').filter(Boolean).forEach((pair) => {
      const cut = pair.indexOf('|');
      const id = pair.slice(0, cut);
      const url = pair.slice(cut + 1);
      axios.get(`${API_BASE}${url}`, { responseType: 'blob' })
        .then((r) => {
          const u = URL.createObjectURL(r.data);
          if (!live) { URL.revokeObjectURL(u); return; }
          made.push(u);
          setPics((p) => ({ ...p, [id]: u }));
        })
        .catch(() => {});
    });
    return () => { live = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [key]);
  return pics;
}

function Counts({ floor }) {
  const t = floor.tones || {};
  return (
    <span className="as-bv-counts">
      <span className="as-bv-devices">{devicesText(floor.devices || 0)}</span>
      {t.warning > 0 && <span className="as-bv-count" data-tone="warning">{t.warning} warning</span>}
      {t.critical > 0 && <span className="as-bv-count" data-tone="critical">{t.critical} critical</span>}
    </span>
  );
}

const POP_GAP = 8;
const POP_EDGE = 8;
/** The stack's own drawing size, before it is scaled to the window. */
const STAGE_W = 480;
const FLOOR_STEP = 64;
const STAGE_EXTRA = 190;
const MIN_SCALE = 0.6;
const MAX_SCALE = 2.2;
const catText = (f) => (Array.isArray(f.by_category) && f.by_category.length > 0
  ? f.by_category.map((c) => `${c.name} ${c.count}`).join(' · ') : '');

// The floor's details stand OUTSIDE the 3D stack: inside a transformed slab the
// popup is clipped between slabs and painted under the floors above. One fixed
// popover is placed from the floor name's own rectangle, to its right (or its
// left when there is no room), and kept inside the window.
function FloorPop({ tip }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const r = tip.rect;
    let left = r.right + POP_GAP;
    if (left + w > vw - POP_EDGE) left = r.left - POP_GAP - w;
    left = Math.max(POP_EDGE, Math.min(left, vw - POP_EDGE - w));
    let top = r.top + (r.height - h) / 2;
    top = Math.max(POP_EDGE, Math.min(top, vh - POP_EDGE - h));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.visibility = 'visible';
  }, [tip]);
  return (
    <div ref={ref} id="as-bv-tip" className="as-bv-pop" role="tooltip" data-testid="floor-pop" data-floor={tip.id}>
      {tip.text}
    </div>
  );
}

export default function BuildingView({ buildings }) {
  const navigate = useNavigate();
  const narrow = useNarrow();
  const all = useMemo(() => (buildings || []).flatMap((b) => b.floors || []), [buildings]);
  const pics = usePlanPictures(all);
  const open = (f) => navigate(`/assets/map/${f.id}`);
  const [tip, setTip] = useState(null);
  // Going from a floor's slab to its name (or across the slabs' overlap) is a
  // leave then an enter: the hide waits a moment so the next show cancels it,
  // and the same floor keeps its popup rather than drawing a new one.
  const hideTimer = useRef(null);
  const show = useCallback((f, e) => {
    clearTimeout(hideTimer.current);
    const text = catText(f);
    if (!text) { setTip(null); return; }
    const row = e.currentTarget.closest('.as-bv-row');
    const anchor = (row && row.querySelector('.as-bv-label')) || e.currentTarget;
    const b = anchor.getBoundingClientRect();
    setTip((t) => (t && t.id === f.id ? t : { id: f.id, text, rect: { left: b.left, right: b.right, top: b.top, height: b.height } }));
  }, []);
  const hide = useCallback(() => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setTip(null), 120);
  }, []);
  useEffect(() => () => clearTimeout(hideTimer.current), []);
  const hideNow = useCallback(() => { clearTimeout(hideTimer.current); setTip(null); }, []);
  useEffect(() => {
    if (!tip) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setTip(null); };
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => { document.removeEventListener('keydown', esc); window.removeEventListener('scroll', hide, true); window.removeEventListener('resize', hide); };
  }, [tip, hide]);
  const label = (f) => `Open ${f.name}, ${devicesText(f.devices || 0)}`;

  // The stack is drawn at a fixed size (480 wide, 64 a floor); it is then
  // scaled to the room the window has for it - the column's width shared by
  // the buildings, and the height below its top - so a building of three
  // floors fills the screen rather than sitting small in it (Gordon, 29 October).
  const rootRef = useRef(null);
  const [scale, setScale] = useState(1);
  const tallest = Math.max(1, ...(buildings || []).map((b) => (b.floors || []).length));
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || narrow) return undefined;
    const fit = () => {
      const count = Math.max(1, (buildings || []).length);
      const r = el.getBoundingClientRect();
      // The buildings share the width, less the 3rem gaps between them and a little slack.
      const w = (r.width - (count - 1) * 56 - 8) / count;
      const h = window.innerHeight - Math.max(0, r.top) - 64;   // the window below the stack's top, less its name
      const s = Math.min(w / STAGE_W, h / (tallest * FLOOR_STEP + STAGE_EXTRA), MAX_SCALE);
      const next = Math.max(MIN_SCALE, Math.floor(s * 100) / 100);
      setScale((v) => (Math.abs(v - next) < 0.02 ? v : next));
    };
    fit();
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    if (watch) watch.observe(el);
    window.addEventListener('resize', fit);
    return () => { if (watch) watch.disconnect(); window.removeEventListener('resize', fit); };
  }, [buildings, narrow, tallest]);

  if (!buildings || buildings.length === 0) return null;

  if (narrow) {
    return (
      <div className="as-bv" data-testid="building-view" data-mode="list">
        {buildings.map((b) => (
          <section key={b.id} className="as-bv-building">
            <h3 className="as-bv-name">{b.name}</h3>
            <ul className="as-bv-list">
              {[...(b.floors || [])].sort(byOrder).reverse().map((f) => (
                <li key={f.id}>
                  <button type="button" className="as-bv-listbtn" aria-label={label(f)} onClick={() => open(f)}>
                    <strong>{f.name}</strong>
                    <Counts floor={f} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="as-bv" data-testid="building-view" data-mode="3d" ref={rootRef} data-scale={scale}>
      {buildings.map((b) => {
        const floors = [...(b.floors || [])].sort(byOrder);
        return (
          <section key={b.id} className="as-bv-building">
            {/* zoom, not a transform: the stack is laid out at the larger size, so
                its slabs overlap exactly as at 1 and every one stays reachable. */}
            <div className="as-bv-fit">
            <div className="as-bv-stage" style={{ height: `${floors.length * FLOOR_STEP + STAGE_EXTRA}px`, zoom: scale === 1 ? undefined : scale }}>
              {floors.map((f, i) => (
                <div key={f.id} className="as-bv-row" data-floor={f.id} style={{ bottom: `${i * 64}px`, zIndex: i + 1 }}>
                  <button type="button" className="as-bv-slab" aria-label={label(f)} onClick={() => open(f)}
                    aria-describedby={tip && tip.id === f.id ? 'as-bv-tip' : undefined}
                    onMouseEnter={(e) => show(f, e)} onMouseLeave={hide} onFocus={(e) => show(f, e)} onBlur={hideNow}>
                    <span className="as-bv-face" data-plan={pics[f.id] ? 'yes' : 'no'}>
                      {pics[f.id] && <img src={pics[f.id]} alt="" />}
                    </span>
                  </button>
                  <button type="button" className="as-bv-label" aria-label={label(f)} onClick={() => open(f)}
                    aria-describedby={tip && tip.id === f.id ? 'as-bv-tip' : undefined}
                    onMouseEnter={(e) => show(f, e)} onMouseLeave={hide} onFocus={(e) => show(f, e)} onBlur={hideNow}>
                    <strong>{f.name}</strong>
                    <Counts floor={f} />
                  </button>
                </div>
              ))}
            </div>
            </div>
            <h3 className="as-bv-name">{b.name}</h3>
          </section>
        );
      })}
      {tip && <FloorPop tip={tip} />}
    </div>
  );
}
